import { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  Link,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonIcon from '@mui/icons-material/Person';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import resolveAssetPath from '../../utils/assetResolver';

const LogoImage = styled('img')(({ theme }) => ({
  height: '45px',
  width: '45px',
  marginRight: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    height: '35px',
    width: '35px',
  },
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  boxShadow: 'none',
  borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  position: 'sticky',
  top: 0,
  zIndex: theme.zIndex.appBar,
}));

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const currentUser = null; // No authentication in frontend-only version
  const isLoggedIn = !!currentUser;
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget);
  };
  
  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };
  
  const handleLogout = () => {
    // No authentication in frontend-only version
    handleProfileMenuClose();
  };

  const menuItems = [
    { label: 'Home', path: '/' },
    { label: 'About Us', path: '/about' },
    { label: 'Careers', path: '/careers' },
    { label: 'Networking', path: '/networking' },
    { label: 'Companies', path: '/companies' },
    { label: 'Contact', path: '/contact' },
  ];

  const drawer = (
    <List>
      {menuItems.map((item) => (
        <ListItem
          key={item.label}
          component={RouterLink}
          to={item.path}
          onClick={handleDrawerToggle}
        >
          <ListItemText primary={item.label} />
        </ListItem>
      ))}
      <Divider sx={{ my: 2 }} />
      {isLoggedIn ? (
        <>
          <ListItem onClick={handleDrawerToggle} component={RouterLink} to="/profile">
            <ListItemText primary="My Profile" />
          </ListItem>
          <ListItem onClick={handleLogout}>
            <ListItemText primary="Logout" />
          </ListItem>
        </>
      ) : (
        <>
          <ListItem onClick={handleDrawerToggle} component={RouterLink} to="/login">
            <ListItemText primary="Log In" />
          </ListItem>
          <ListItem onClick={handleDrawerToggle} component={RouterLink} to="/signup">
            <ListItemText primary="Sign Up" />
          </ListItem>
        </>
      )}
    </List>
  );

  return (
    <StyledAppBar>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Link component={RouterLink} to="/" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <LogoImage src="/images/logo/vertechie-logo.svg" alt="VerTechie Logo" />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: '1.5rem',
                color: '#0077B5',
              }}
            >
              VerTechie
            </Typography>
          </Link>
        </Box>

        {isMobile ? (
          <IconButton
            color="primary"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
          >
            <MenuIcon />
          </IconButton>
        ) : (
          <Box sx={{ display: { xs: 'none', md: 'flex' }, ml: 4 }}>
            {menuItems.map((item) => (
              <Button
                key={item.path}
                component={RouterLink}
                to={item.path}
                sx={{
                  color: 'text.primary',
                  '&:hover': {
                    color: 'primary.main',
                  },
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isLoggedIn ? (
            <>
              <IconButton
                size="large"
                color="primary"
                sx={{ 
                  mr: 2,
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-2px)' } 
                }}
              >
                <Badge badgeContent={3} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
              <IconButton
                onClick={handleProfileMenuOpen}
                size="small"
                edge="end"
                aria-label="account of current user"
                aria-haspopup="true"
                color="primary"
                sx={{ 
                  p: 0.5,
                  border: '2px solid #0077B5',
                  transition: 'all 0.2s',
                  '&:hover': { 
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 8px rgba(0, 119, 181, 0.2)'
                  } 
                }}
              >
                <Avatar sx={{ bgcolor: '#0077B5', width: 32, height: 32 }}>
                  <PersonIcon fontSize="small" />
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={profileMenuAnchor}
                open={Boolean(profileMenuAnchor)}
                onClose={handleProfileMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                  elevation: 3,
                  sx: { 
                    mt: 1.5, 
                    minWidth: 180,
                    borderRadius: 2,
                    '& .MuiMenuItem-root': {
                      py: 1,
                      px: 2
                    }
                  }
                }}
              >
                <MenuItem onClick={handleProfileMenuClose} component={RouterLink} to="/profile">
                  <AccountCircleIcon fontSize="small" sx={{ mr: 1.5 }} />
                  My Profile
                </MenuItem>
                <MenuItem onClick={handleProfileMenuClose} component={RouterLink} to="/my-courses">
                  <BookmarkIcon fontSize="small" sx={{ mr: 1.5 }} />
                  My Courses
                </MenuItem>
                <Divider sx={{ my: 1 }} />
                <MenuItem onClick={handleLogout}>
                  <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} />
                  Logout
                </MenuItem>
              </Menu>
              <Typography variant="body1" sx={{ ml: 1 }}>
                User
              </Typography>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                color="primary"
                component={RouterLink}
                to="/login"
                sx={{
                  mr: 2,
                  fontWeight: 500,
                  borderRadius: '20px',
                  borderColor: '#0077B5',
                  color: '#0077B5',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 119, 181, 0.08)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 8px rgba(0, 119, 181, 0.1)'
                  }
                }}
              >
                Log In
              </Button>
              <Button
                variant="contained"
                color="primary"
                component={RouterLink}
                to="/signup"
                sx={{
                  fontWeight: 600,
                  color: 'white',
                  bgcolor: '#0077B5',
                  borderRadius: '20px',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: '#005885',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 10px rgba(0, 119, 181, 0.3)'
                  }
                }}
              >
                Sign Up
              </Button>
            </>
          )}
        </Box>
      </Toolbar>
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: { width: 250 }
        }}
      >
        {drawer}
      </Drawer>
    </StyledAppBar>
  );
};

export default Navbar; 